<template>
    <div
        v-if="open"
        class="fixed top-0 w-full h-full bg-black/50 z-20 flex justify-center items-center">
        <template v-if="currentStep==FIRST">
            <div class="border rounded-lg shadow shadow-gray-100 bg-white w-[550px]">
                <div class="px-5 py-5 flex justify-between items-center">
                    <h2 class="font-inter-heading-16"> New Brand Added </h2>
                    <CloseIcon weight="FILL" @click="hidePopup" class="cursor-pointer" />
                </div>
                <hr />
                <div class="px-4 items-center py-2.5 text-gray-600 font-inter-heading-20">
                    Please turn on/off per client to display to employees.
                </div>
                <div
                    class="bg-background-basic-2 border-y border-gray-07 text-content-basic-2 h-10 px-3 leading-10">
                    Brand Name
                </div>
                <div class="min-h-[20rem] max-h-[70vh] overflow-y-auto">
                    <div
                        v-for="(brand, index) in unSetBrandList"
                        :key="index"
                        class="flex justify-between px-4 h-16 items-center border-b border-gray-07">
                        <div class="font-inter-heading-20"> {{ brand.brandName }} </div>
                        <DesignButton
                            btnType="PRIMARY_LINE"
                            btnSize="LG"
                            btnText="Go to Setting"
                            :btnDisabled="false" 
                            @click-event="goToSecondStep(brand)"
                        />
                    </div>
                </div>
                <hr/>
                <div class="flex justify-end px-5 py-3">
                    <DesignButton
                        btnType="QUATERNARY"
                        btnSize="LG"
                        btnText="Close"
                        :btnDisabled="false" 
                        @click-event="hidePopup"
                    />
                </div>
            </div>
        </template>
        <template v-if="currentStep == SECOND">
            <div class="border rounded-lg shadow shadow-gray-100 bg-white w-[560px]">
                <div class="px-5 py-5 flex justify-between items-center">
                    <h2 class="font-inter-heading-6"> New Brand Added </h2>
                    <CloseIcon weight="FILL" @click="hidePopup" class="cursor-pointer" />
                </div>
                <hr />
                <div class="max-h-[70vh] px-5 pt-5 overflow-y-auto">
                    <div class="flex flex-col gap-4">
                        <div>
                            <p class="font-inter-body-5 text-primary-3">Brand Name</p>
                            <p class="font-inter-heading-6">{{ brandToBlock.brandName }}</p>
                        </div>
                        <div class="flex justify-between items-baseline w-full border-content-basic-4 border rounded-01_5">
                            <input
                                v-model="searchText"
                                placeholder="Search Company"
                                class="w-full font-inter-body-2 text-content-basic-1 placeholder:text-content-basic-3 px-6 border-none outline-none focus:ring-0"
                                @focus="isFocused = true"
                                @blur="isFocused = false"
                                @keyup.enter="searchBrandListByName"
                            />
                            <DesignButton 
                                btnText="Search"
                                btnType="WHITE"
                                btnSize="LG"
                                class="rounded-l-none border-l border-content-basic-4 "
                                @click-event="searchBrandListByName"
                                :btnDisabled="false"
                            />
                        </div>
                        <table>
                            <colgroup>
                                <col class="w-[23%]" />
                                <col class="w-[78%]" />
                            </colgroup>
                            <thead>
                                <tr
                                    class="bg-background-basic-2 text-background-inverse-3 border-y border-content-basic-2">
                                    <th scope="col" class="pl-3 h-10 text-center font-inter-body-4">Brand Setting</th>
                                    <th scope="col" class="pl-5 h-10 text-center font-inter-body-4">Company</th>
                                </tr>
                            </thead>
                            <tbody class="">
                                <tr
                                    v-for="(company, index) in companyListToBlockBrand"
                                    :key="index"
                                    class="border-b border-border-basic-1 last:border-b-0 h-10">
                                    <td class="">
                                        <div class="w-full flex justify-center">
                                            <ElementsToggle v-model="company.block" :true-value="false" :false-value="true" />
                                        </div>
                                    </td>
                                    <td class="pl-5 font-inter-body-3 text-black">
                                        {{ company.companyName }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <hr />
                <div class="flex justify-end gap-2 px-4 py-3">
                    <DesignButton
                        btnText="Previous"
                        btnType="QUATERNARY"
                        btnSize="LG"
                        @click-event="goBackToFirstStep()"
                        :btnDisabled="false">
                        <template v-slot:front-icon>
                            <ChevronLeftIcon width="12" height="12" weight="FILL" />
                        </template>
                    </DesignButton>
                    <DesignButton
                        btnText="Confirm"
                        btnType="PRIMARY"
                        btnSize="LG"
                        @click-event="goToThirdStep"
                        :btnDisabled="false" />
                </div>
            </div>
        </template>
        <template v-else-if="currentStep == THIRD">
            <div class="border rounded-lg shadow shadow-gray-100 bg-white w-[360px]">
                <div class="pt-6 px-5 pb-4 gap-5 flex flex-col">
                    <div class="font-inter-heading-13 text-center w-full">
                        Brand Setting Completed
                    </div>
                    <div class="font-inter-heading-20 text-gray-600 text-center">
                        The brand is now available to employees in FlexBen.
                    </div>
                    <DesignButton
                        btnType="PRIMARY"
                        btnSize="LG"
                        btnText="OK"
                        @click-event="onClickComplete"
                        :btnDisabled="false" />
                </div>
            </div>
        </template>
        
    </div>
</template>

<script>
import CloseIcon from '@/components/design-system/icon/close.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import ChevronLeftIcon from '@/components/design-system/icon/chevron-left.vue';
import ElementsToggle from '@/components/elements/ElementsToggle.vue';

export default {
    components: {
        CloseIcon,
        DesignButton,
        ChevronLeftIcon,
        ElementsToggle,
    },
    emits : ["afterClose"],
    props : {
        open : { type : Boolean, default : false },
        unSetBrandList : { type : Array, default : function(){ return []}},
    },
    watch : {
        open( newVal ){
            if( !newVal ) return;
            this.currentStep = this.FIRST;
            this.brandToBlock = null;
            this.getCompanyAllList();
        }
    },
    data() {
        return {
            currentStep: 'FIRST',
            FIRST: 'FIRST',
            SECOND: 'SECOND',
            THIRD: 'THIRD',
            companyListAll : [],
            brandToBlock : null,
            searched_brand_list: [],
            searchText: '',
            companyListToBlockBrand : [],
        };
    },
    methods: {
        hidePopup() {
            const self = this;
            self.$emit("afterClose");
        },
        searchBrandListByName() {
            const self = this;
            if( self.searchText == null || self.searchText == "" || self.searchText == undefined ){
                self.companyListToBlockBrand = self.companyListAll;
                return;
            }
            self.companyListToBlockBrand = self.companyListAll.filter(com=>{
                console.log(com, this.searchText)
                return com.companyName.toUpperCase().includes(self.searchText.toUpperCase());
            })
        },
        goToSecondStep(brand){
            const self = this;
            self.brandToBlock = brand;
            self.currentStep = self.SECOND;
            self.searchText = '';
            self.companyListAll = self.companyListAll.map(i=> ({ ...i, "block" : false }));
            self.searchBrandListByName();
        },
        goBackToFirstStep(){
            const self = this;
            self.currentStep = self.FIRST;
            self.brandToBlock = null;
        },
        goToThirdStep(){
            const self = this;
            const {brandId, flexbenBrandSeq} = self.brandToBlock;
            const companySeqList = self.companyListToBlockBrand.filter(i=>i.block).map(i=>i.companySeq);
            const updateForm = {brandId, flexbenBrandSeq,companySeqList}

            const url = self.$api("uri", "post-update-brand-block-company-of-brand");
            self.$axios.post(url, updateForm)
            .then(res=> {
                self.$emit('update:unSetBrandList', res.data.data);
                self.currentStep = self.THIRD;
            })
            .catch(alert)
        },
        onClickComplete(){
            const self = this;
            const unSetCnt = self.unSetBrandList.length;
            if( unSetCnt > 0 ){
                self.goBackToFirstStep();
                return;
            }
            self.hidePopup();
        },
        getCompanyAllList(){
            const uri = this.$api("uri", "get-company-all");
            this.$axios.get(uri).then(res=>{
                this.companyListAll = res.data.data.list.map(i=> ({ ...i, "block" : false }));
            })
            .catch(alert);
        },
    },
};
</script>
